/**
 * Platform-managed security policies for offline auth & session management.
 *
 * Policies are fetched from the Platform Edge Function after login and cached
 * in localStorage for offline access. Zoo admins CANNOT modify these —
 * only platform (overlord) admins can change security policies.
 *
 * Defaults are fail-secure: if the policy cannot be fetched (offline first launch),
 * the strictest defaults apply.
 */

const STORAGE_KEY = 'zbk3_security_policy';

const PLATFORM_URL = process.env.PLATFORM_SUPABASE_URL || '';

const PLATFORM_ANON_KEY = process.env.PLATFORM_SUPABASE_ANON_KEY || '';

/**
 * Shared device policy overlay — set by platform admin per device/org.
 * When null (default), behavior is adaptive based on identity count.
 * When set, enforces shared workstation restrictions.
 */
export interface SharedDevicePolicy {
  /** Idle timeout in minutes before auto-logout (default 15) */
  enforceIdleTimeoutMinutes: number;
  /** Show sign-out button prominently in header (default true) */
  prominentSignOut: boolean;
  /** Disable biometric auth on this device (default true for shared) */
  disableBiometric: boolean;
  /** Disable offline login (default true for shared) */
  disableOfflineLogin: boolean;
  /** Pre-fill email domain for "Someone else" (e.g. "zoo-liberec.cz") */
  emailDomainHint: string | null;
  /** Max identities in account chooser (default 8) */
  maxIdentities: number;
  /** Auto-remove identities after N days without login (default 30) */
  identityExpiryDays: number;
}

export interface SecurityPolicy {
  /** Max session lifetime in hours (default 168 = 7 days) */
  sessionTimeoutHours: number;
  /** Max hours the app can operate offline before forcing re-auth (default 72) */
  maxOfflineHours: number;
  /** Lock app when it goes to background (default true) */
  requireLockOnBackground: boolean;
  /** Grace period in seconds before lock activates (default 30) */
  lockGracePeriodSeconds: number;
  /** Allow offline login via cached credentials (default true) */
  offlineLoginEnabled: boolean;
  /** Max age of cached credentials in hours before they expire (default 720 = 30 days) */
  offlineLoginMaxAgeHours: number;
  /** Max queued offline mutations before blocking new ones (default 500) */
  maxOfflineMutations: number;
  /** Require encryption of cached data at rest (default true) */
  requireEncryptionAtRest: boolean;
  /** Shared device policy overlay — null means personal device behavior */
  sharedDevicePolicy: SharedDevicePolicy | null;
}

/**
 * Fail-secure defaults — used when policy cannot be fetched.
 * These are intentionally strict to protect data in case of first offline launch.
 */
export const DEFAULT_SHARED_DEVICE_POLICY: SharedDevicePolicy = {
  enforceIdleTimeoutMinutes: 15,
  prominentSignOut: true,
  disableBiometric: true,
  disableOfflineLogin: true,
  emailDomainHint: null,
  maxIdentities: 8,
  identityExpiryDays: 30,
};

export const DEFAULT_POLICY: SecurityPolicy = {
  sessionTimeoutHours: 168,
  maxOfflineHours: 72,
  requireLockOnBackground: true,
  lockGracePeriodSeconds: 43200,
  offlineLoginEnabled: true,
  offlineLoginMaxAgeHours: 720,
  maxOfflineMutations: 500,
  requireEncryptionAtRest: true,
  sharedDevicePolicy: null, // null = personal device behavior (default)
};

/**
 * Fetch security policy from Platform Edge Function.
 * Called after login when online.
 */
export async function fetchSecurityPolicy(orgId: string): Promise<SecurityPolicy> {
  const response = await fetch(
    `${PLATFORM_URL}/functions/v1/security-policies?org_id=${encodeURIComponent(orgId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${PLATFORM_ANON_KEY}`,
        apikey: PLATFORM_ANON_KEY,
      },
    },
  );

  if (!response.ok) {
    console.warn('[SecurityPolicy] Failed to fetch policy, using defaults:', response.status);
    return DEFAULT_POLICY;
  }

  const data = (await response.json()) as Record<string, unknown>;
  return parsePolicyResponse(data);
}

function parseSharedDevicePolicy(
  data: Record<string, unknown> | null | undefined,
): SharedDevicePolicy | null {
  if (!data || typeof data !== 'object') return null;
  return {
    enforceIdleTimeoutMinutes: asNumber(
      data.enforce_idle_timeout_minutes,
      DEFAULT_SHARED_DEVICE_POLICY.enforceIdleTimeoutMinutes,
    ),
    prominentSignOut: asBoolean(
      data.prominent_sign_out,
      DEFAULT_SHARED_DEVICE_POLICY.prominentSignOut,
    ),
    disableBiometric: asBoolean(
      data.disable_biometric,
      DEFAULT_SHARED_DEVICE_POLICY.disableBiometric,
    ),
    disableOfflineLogin: asBoolean(
      data.disable_offline_login,
      DEFAULT_SHARED_DEVICE_POLICY.disableOfflineLogin,
    ),
    emailDomainHint: typeof data.email_domain_hint === 'string' ? data.email_domain_hint : null,
    maxIdentities: asNumber(
      data.max_identities,
      DEFAULT_SHARED_DEVICE_POLICY.maxIdentities,
    ),
    identityExpiryDays: asNumber(
      data.identity_expiry_days,
      DEFAULT_SHARED_DEVICE_POLICY.identityExpiryDays,
    ),
  };
}

function parsePolicyResponse(data: Record<string, unknown>): SecurityPolicy {
  return {
    sessionTimeoutHours: asNumber(data.session_timeout_hours, DEFAULT_POLICY.sessionTimeoutHours),
    maxOfflineHours: asNumber(data.max_offline_hours, DEFAULT_POLICY.maxOfflineHours),
    requireLockOnBackground: asBoolean(
      data.require_lock_on_background,
      DEFAULT_POLICY.requireLockOnBackground,
    ),
    lockGracePeriodSeconds: asNumber(
      data.lock_grace_period_seconds,
      DEFAULT_POLICY.lockGracePeriodSeconds,
    ),
    offlineLoginEnabled: asBoolean(data.offline_login_enabled, DEFAULT_POLICY.offlineLoginEnabled),
    offlineLoginMaxAgeHours: asNumber(
      data.offline_login_max_age_hours,
      DEFAULT_POLICY.offlineLoginMaxAgeHours,
    ),
    maxOfflineMutations: asNumber(data.max_offline_mutations, DEFAULT_POLICY.maxOfflineMutations),
    requireEncryptionAtRest: asBoolean(
      data.require_encryption_at_rest,
      DEFAULT_POLICY.requireEncryptionAtRest,
    ),
    sharedDevicePolicy: parseSharedDevicePolicy(
      data.shared_device_policy as Record<string, unknown> | null,
    ),
  };
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Cache policy in localStorage for offline access.
 */
export function storeSecurityPolicy(policy: SecurityPolicy): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(policy));
  } catch {
    console.warn('[SecurityPolicy] Failed to store policy in localStorage');
  }
}

/**
 * Retrieve cached policy. Returns DEFAULT_POLICY if none cached.
 */
export function getStoredSecurityPolicy(): SecurityPolicy {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_POLICY;
    const parsed = JSON.parse(raw) as Partial<SecurityPolicy>;
    // Merge with defaults to handle schema evolution (new fields added later)
    return { ...DEFAULT_POLICY, ...parsed };
  } catch {
    return DEFAULT_POLICY;
  }
}

/**
 * Clear cached policy (on zoo switch / signout).
 */
export function clearSecurityPolicy(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}
